import { CloseOutlined, PaperClipOutlined, SendOutlined } from "@ant-design/icons";
import { Alert, Button, Input } from "antd";
import type React from "react";
import { useRef, useState } from "react";
import { ACCEPTED_IMAGE_TYPES, attachmentProblem, dataUrlToImage } from "../assistant-attachments";
import type { QuestionImage } from "../assistant-types";

interface Attachment {
  id: string;
  previewUrl: string;
  image: QuestionImage;
}

interface ComposerProps {
  sending: boolean;
  onSend: (text: string, images: QuestionImage[]) => void;
}

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });

export const Composer: React.FC<ComposerProps> = ({ sending, onSend }) => {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [problem, setProblem] = useState<string>();
  const fileInput = useRef<HTMLInputElement>(null);

  const attach = async (files: File[]) => {
    let accepted = attachments;
    for (const file of files) {
      const issue = attachmentProblem({ type: file.type, size: file.size }, accepted.length);
      if (issue) {
        setProblem(issue);
        continue;
      }
      const previewUrl = await readAsDataUrl(file);
      const image = dataUrlToImage(previewUrl);
      if (image) accepted = [...accepted, { id: crypto.randomUUID(), previewUrl, image }];
    }
    setAttachments(accepted);
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const files = Array.from(event.clipboardData.files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (files.length === 0) return;
    event.preventDefault();
    void attach(files);
  };

  const canSend = !sending && (text.trim() !== "" || attachments.length > 0);

  const send = () => {
    if (!canSend) return;
    onSend(
      text.trim(),
      attachments.map((attachment) => attachment.image),
    );
    setText("");
    setAttachments([]);
    setProblem(undefined);
  };

  return (
    <div className="flex flex-col gap-2">
      {problem && (
        <Alert
          type="error"
          showIcon
          closable
          message={problem}
          onClose={() => setProblem(undefined)}
        />
      )}
      {attachments.length > 0 && (
        <div className="flex gap-2">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="relative">
              <img
                src={attachment.previewUrl}
                alt="Screenshot to send"
                className="h-14 w-14 rounded border object-cover"
              />
              <Button
                size="small"
                shape="circle"
                icon={<CloseOutlined />}
                aria-label="Remove screenshot"
                className="absolute -right-2 -top-2"
                onClick={() =>
                  setAttachments((current) => current.filter((item) => item.id !== attachment.id))
                }
              />
            </div>
          ))}
        </div>
      )}
      <Input.TextArea
        value={text}
        onChange={(event) => setText(event.target.value)}
        onPaste={handlePaste}
        onPressEnter={(event) => {
          if (!event.shiftKey) {
            event.preventDefault();
            send();
          }
        }}
        autoSize={{ minRows: 2, maxRows: 6 }}
        placeholder="Ask your questions here… (Enter to send, paste a screenshot)"
        disabled={sending}
      />
      <div className="flex justify-between">
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(",")}
          multiple
          hidden
          onChange={(event) => {
            void attach(Array.from(event.target.files ?? []));
            event.target.value = "";
          }}
        />
        <Button
          icon={<PaperClipOutlined />}
          onClick={() => fileInput.current?.click()}
          disabled={sending}
        >
          Screenshot
        </Button>
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={send}
          loading={sending}
          disabled={!canSend}
        >
          Ask
        </Button>
      </div>
    </div>
  );
};
